import {
  SecurityFindingKind,
  type SecurityFindingRecommendation,
  type SecurityFindingSeverityLevel,
  type SecurityFindingImpactLevel,
  type SensitiveTopic,
  type DocumentWithLargeScaleCollaboration,
  type Permission,
  type SecurityFindingResource,
} from "$lib/models/base";
import {
  collection,
  getCountFromServer,
  getDocs,
  getFirestore,
  limit,
  query,
  where,
  orderBy,
  startAfter,
  type Timestamp,
  type QueryDocumentSnapshot,
  type QueryConstraint,
} from "firebase/firestore";
import {
  computeImpactScorePerKind,
  mapSeverityScoreToLevel,
  summariseExternalPrincipals,
} from "$lib/utils/securityFindings";
import { derived, readable, writable } from "svelte/store";
import { selectedReportVersionStore } from "./orgSecurityReports";
import { chainStores, getSubCollectionAsStore } from "$lib/utils/firestore";
import { selectedOrgIdStore } from "./orgs";
import { firebaseApp } from "$lib/utils/env";
import { filter, map } from "lodash";

export const MAX_FINDINGS_TO_FETCH = 40_000;
export const BATCH_SIZE = 500; // Load 500 findings per batch
export const DELAY_BETWEEN_BATCHES = 200; // 200ms delay between batches to prevent overwhelming the UI

/**
 * The one finding kind the Anomalous Permissions page shows (#2270 section 10). Public and
 * external-principal exposures are reported on the Exposed Sensitive Documents page; they and the
 * modelled-but-never-generated kinds are hidden by filter, never deleted.
 *
 * v3 reads carry this as a Firestore equality next to the version equality (two equalities, no
 * `orderBy`: automatic single-field indexes). v2 reads filter after load: the v2 batch loader
 * orders by `created`, so an equality there would need a composite index that does not exist, and
 * the v2 count stays unfiltered because the loader stops once loaded raw docs reach it.
 */
export const ANOMALOUS_PERMISSIONS_PAGE_KIND = SecurityFindingKind.ANOMALOUS_PERMISSIONS_ON_SENSITIVE_DOCUMENT;

/** v3 reads already carry the kind equality; v2 reads are unfiltered by the query, so filter here. */
const onlyAnomalousPermissionsPageKind = (findings: SecurityFinding[], schemaVersion: string) =>
  schemaVersion === "v3"
    ? findings
    : findings.filter((finding) => finding.securityFindingKind === ANOMALOUS_PERMISSIONS_PAGE_KIND);

export interface SecurityFinding {
  id: string;
  securityFindingId: string;
  securityFindingVersion: string;
  securityFindingKind: SecurityFindingKind;
  severity: number;
  severityLevel: SecurityFindingSeverityLevel;
  confidence: number;
  resources: SecurityFindingResource[];
  recommendations: SecurityFindingRecommendation[];
  created: Timestamp;
  modified: Timestamp;
  system: string;
  status?: string;
  authorEmail?: string;

  // Anomalous permissions on sensitive document
  permissions?: Permission[];
  numberOfMisconfiguredPermissions?: number;
  numberOfImpactedPrincipals?: number;
  totalNumberOfPermissions?: number;
  totalNumberOfPrincipals?: number;

  // Large scale collaboration on personal drive
  numberOfDocumentsInPersonalDrive?: number;
  learnedThreshold?: number;
  documents?: DocumentWithLargeScaleCollaboration[];

  // Personal drive with sensitive documents
  sensitiveDocuments?: SecurityFindingResource[];
  permissionsToSensitiveDocuments?: Permission[];
  totalNumberOfDocuments?: number;

  // computed fields
  sensitiveTopics?: SensitiveTopic[];
  impact?: number;
  impactLevel?: SecurityFindingImpactLevel;
  totalNumberOfSensitiveDocuments?: number;
  numberOfExternalPrincipals?: number;
}

const getSchemaVersion = async (orgId: string, securityFindingVersion: string) => {
  const v3SecurityFindings = await getDocs(
    query(
      collection(getFirestore(await firebaseApp), `organisations/${orgId}/security_findings`),
      where("securityFindingVersion", "==", securityFindingVersion),
      limit(1),
    ),
  );
  if (!v3SecurityFindings.empty) {
    return v3SecurityFindings.docs[0].data().schemaVersion;
  } else {
    return "v2";
  }
};

const selectedSchemaVersionStore = chainStores(
  derived([selectedOrgIdStore, selectedReportVersionStore], (both) => both),
  undefined,
  ([selectedOrgId, selectedReportVersion]) => {
    if (!selectedOrgId || !selectedReportVersion) {
      return readable(undefined);
    }

    let schemaVersionStore = writable<string | undefined>(undefined);
    getSchemaVersion(selectedOrgId, selectedReportVersion).then((schemaVersion) => {
      if (schemaVersion) {
        schemaVersionStore.set(schemaVersion);
      }
    });
    return schemaVersionStore;
  },
);

const checkEmptyFindings = async (orgId: string) => {
  const docCount = await getCountFromServer(
    query(collection(getFirestore(await firebaseApp), `organisations/${orgId}/security_findings`)),
  );
  console.log("[checkEmptyFindings] ** DOC COUNT **", docCount.data().count);
  return docCount.data().count === 0;
};

export const emptyFindingsStore = chainStores(
  derived([selectedOrgIdStore], (both) => both),
  undefined,
  ([selectedOrgId]) => {
    if (!selectedOrgId) {
      return readable(undefined);
    }
    let isEmptyStore = writable<boolean>(false);
    const startTime = performance.now();
    checkEmptyFindings(selectedOrgId).then((isEmpty) => {
      const endTime = performance.now();
      console.log("[emptyFindingsStore] ** Time taken **", endTime - startTime);
      isEmptyStore.set(isEmpty);
    });
    return isEmptyStore;
  },
);

const countFindingsByVersion = async (orgId: string, version: string, schemaVersion: string) => {
  if (schemaVersion === "v3") {
    const queryRef = query(
      collection(getFirestore(await firebaseApp), `organisations/${orgId}/security_findings`),
      where("securityFindingVersion", "==", version),
      where("securityFindingKind", "==", ANOMALOUS_PERMISSIONS_PAGE_KIND),
    );
    const docCount = await getCountFromServer(queryRef);
    return docCount.data().count;
  } else {
    const queryRef = query(
      collection(getFirestore(await firebaseApp), `organisations/${orgId}/security_findings`),
      where("security_finding_reference.security_finding_version", "==", version),
    );
    const docCount = await getCountFromServer(queryRef);
    return docCount.data().count;
  }
};

export const selectedVersionFindingCountStore = chainStores(
  derived([selectedOrgIdStore, selectedReportVersionStore, selectedSchemaVersionStore], (both) => both),
  undefined,
  ([selectedOrgId, selectedReportVersion, selectedSchemaVersion]) => {
    if (!selectedOrgId || !selectedReportVersion || !selectedSchemaVersion) {
      return readable(undefined);
    }
    const startTime = performance.now();
    let countStore = writable<number | undefined>(undefined);
    countFindingsByVersion(selectedOrgId, selectedReportVersion, selectedSchemaVersion).then((count) => {
      const endTime = performance.now();
      console.log("[selectedVersionFindingCountStore] ** Time taken **", endTime - startTime);
      countStore.set(count);
    });
    return countStore;
  },
);

const rawOrgSecurityFindingsStore = chainStores(
  derived([selectedOrgIdStore, selectedReportVersionStore, selectedSchemaVersionStore], (both) => both),
  undefined,
  ([selectedOrgId, selectedReportVersion, selectedSchemaVersion]) => {
    console.log("[orgSecurityFindingsStore] ** SELECTED ORG ID **", selectedOrgId);
    console.log("[orgSecurityFindingsStore] ** SELECTED REPORT VERSION **", selectedReportVersion);
    console.log("[orgSecurityFindingsStore] ** SELECTED SCHEMA VERSION **", selectedSchemaVersion);
    if (!selectedOrgId || !selectedReportVersion || !selectedSchemaVersion) {
      return readable(undefined);
    }

    if (selectedSchemaVersion === "v3") {
      return getSubCollectionAsStore(`organisations/${selectedOrgId}/security_findings`, {
        where: [
          { field: "securityFindingVersion", op: "==", value: selectedReportVersion },
          { field: "securityFindingKind", op: "==", value: ANOMALOUS_PERMISSIONS_PAGE_KIND },
        ],
      });
    } else {
      return getSubCollectionAsStore(`organisations/${selectedOrgId}/security_findings`, {
        where: [
          { field: "security_finding_reference.security_finding_version", op: "==", value: selectedReportVersion },
        ],
      });
    }
  },
);

export const orgSecurityFindingsStore = derived(
  [rawOrgSecurityFindingsStore, selectedVersionFindingCountStore],
  ([docs, selectedVersionFindingCount]) => {
    if (!docs || docs.length === 0 || !selectedVersionFindingCount) {
      return [];
    }

    console.log("[orgSecurityFindingsStore] ** DOCS **", docs.length, selectedVersionFindingCount);
    if (docs.length !== selectedVersionFindingCount) {
      console.log(
        "[orgSecurityFindingsStore] ** DOCS LENGTH DOES NOT MATCH SELECTED VERSION FINDING COUNT **",
        docs.length,
        selectedVersionFindingCount,
      );
      return [];
    }
    const startTime = performance.now();
    const firstDoc = docs[0];
    const schemaVersion = firstDoc.data.schema_version || firstDoc.data.schemaVersion;
    const findings =
      schemaVersion === "v3"
        ? map(docs, (doc) => {
            const data = doc.data;
            // v3 schema
            // Extract document info from the first resource
            const sensitiveTopics = data.resources
              .map(
                (resource: SecurityFindingResource) =>
                  resource?.sensitiveTopics?.map((topic: SensitiveTopic) => ({
                    sensitiveTopic: topic.sensitiveTopic,
                    sensitivityScore: topic.sensitivityScore,
                  })) || [],
              )
              .flat();
            const system = data.resources[0].resourceId.split(":")[0]; // e.g. "msgraph-v4" from e.g. msgraph-v4:document:{connection_id}:{document_id})

            let securityFindingKindBasedData = {};
            if (data.securityFindingKind === SecurityFindingKind.ANOMALOUS_PERMISSIONS_ON_SENSITIVE_DOCUMENT) {
              securityFindingKindBasedData = {
                numberOfMisconfiguredPermissions: data.numberOfMisconfiguredPermissions || 0,
                numberOfImpactedPrincipals: data.numberOfImpactedPrincipals || 0,
                totalNumberOfPermissions: data.totalNumberOfPermissions || 0,
                totalNumberOfPrincipals: data.totalNumberOfPrincipals || 0,
                permissions: data.permissions || [],
                recommendations: data.permissions?.map((p: Permission & { recommendedAction?: string }) => ({
                  email: p.principalEmail,
                  recommendedAction: p.recommendedAction,
                })),
              };
            } else if (data.securityFindingKind === SecurityFindingKind.DOCUMENT_ACCESSED_BY_EXTERNAL_PRINCIPAL) {
              securityFindingKindBasedData = summariseExternalPrincipals(data.permissions);
            } else if (data.securityFindingKind === SecurityFindingKind.LARGE_SCALE_COLLABORATION_ON_PERSONAL_DRIVE) {
              securityFindingKindBasedData = {
                documents: data.documents || [],
                numberOfDocumentsInPersonalDrive: data.numberOfDocumentsInPersonalDrive || 0,
                learnedThreshold: data.learnedThreshold || 0,
              };
            } else if (
              data.securityFindingKind === SecurityFindingKind.PERMISSIONS_MANAGED_THROUGH_SECONDARY_IDENTITY
            ) {
              securityFindingKindBasedData = {
                permissions: data.permissions || [],
                totalNumberOfPermissions: data.permissions?.length || 0,
              };
            } else if (data.securityFindingKind === SecurityFindingKind.PERSONAL_DRIVE_WITH_SENSITIVE_DOCUMENTS) {
              securityFindingKindBasedData = {
                sensitiveDocuments: data.sensitiveDocuments || [],
                permissionsToSensitiveDocuments: data.permissionsToSensitiveDocuments || [],
                totalNumberOfDocuments: data.totalNumberOfDocuments || 0,
                totalNumberOfSensitiveDocuments: data.totalNumberOfSensitiveDocuments || 0,
              };
            }
            return {
              id: doc.id,
              system,
              securityFindingId: data.securityFindingId,
              securityFindingVersion: data.securityFindingVersion,
              securityFindingKind: data.securityFindingKind,
              modified: data.modified,
              severity: data.severity,
              severityLevel: mapSeverityScoreToLevel(data.severity),
              confidence: data.confidence,
              resources: data.resources,
              created: data.created,
              sensitiveTopics,
              status: data.status,
              ...securityFindingKindBasedData,
            } as SecurityFinding;
          })
        : map(docs, (doc) => {
            // v1, v2 schema
            const data = doc.data;
            return {
              id: doc.id,
              system: data.security_finding_reference?.system,
              securityFindingId: data.security_finding_reference?.security_finding_id,
              securityFindingVersion: data.security_finding_reference?.security_finding_version,
              securityFindingKind: data.security_finding_reference?.security_finding_kind,
              modified: data.modified,
              severity: data.severity,
              severityLevel: mapSeverityScoreToLevel(data.severity),
              confidence: data.confidence,
              resources: [
                {
                  resourceId: doc.id,
                  resourceVersion: data.source_features?.version,
                  createdAt: data.source_features?.created_at,
                  modifiedAt: data.source_features?.modified_at,
                  name: data.source_features?.document_name,
                  path: data.source_features?.document_path,
                  link: data.source_features?.document_link,
                  owner: data.source_features?.author_email,
                  ownerType: "user",
                  ownerEmail: data.source_features?.author_email,
                  isSensitive: data.source_features?.is_sensitive,
                  sensitiveTopics: data.source_features?.sensitive_topics?.map((topic: any) => ({
                    sensitiveTopic: topic.sensitive_topic,
                    sensitivityScore: topic.sensitivity_score,
                  })),
                },
              ],
              numberOfMisconfiguredPermissions: data.stats?.number_of_misconfigured_permissions || 0,
              numberOfImpactedPrincipals: data.stats?.number_of_impacted_principals || 0,
              totalNumberOfPermissions: data.stats?.total_number_of_permissions || 0,
              totalNumberOfPrincipals: data.stats?.total_number_of_principals || 0,
              created: data.created,
              recommendations: data.recommendations,
              sensitiveTopics: data.source_features?.sensitive_topics?.map((topic: any) => ({
                sensitiveTopic: topic.sensitive_topic,
                sensitivityScore: topic.sensitivity_score,
              })),
              isSensitiveDocument: data.source_features?.is_sensitive,
              status: data.status,
            } as SecurityFinding;
          });
    // The v2 count is unfiltered too, so the length check above holds before this filter.
    const shownFindings = onlyAnomalousPermissionsPageKind(findings, schemaVersion);
    const endTime = performance.now();
    console.log("[orgSecurityFindingsStore] ** Time taken **", endTime - startTime);
    return computeImpactScorePerKind(shownFindings);
  },
);

// Batch loading interface
interface BatchLoadingState {
  findings: SecurityFinding[];
  isLoading: boolean;
  hasMore: boolean;
  totalCount: number;
  loadedCount: number;
  lastDocument?: QueryDocumentSnapshot;
  error?: string;
  loadingProgress?: number; // Percentage of completion
}

const createBatchSecurityFindingsStore = (
  orgId: string,
  reportVersion: string,
  schemaVersion: string,
  totalCount: number,
) => {
  // A zero count leaves nothing to load, so it is fully loaded: a v3 org whose findings are all
  // hidden kinds counts 0 here, and the loader never runs to move the progress on.
  const initialLoadingProgress = totalCount > 0 ? 0 : 100;
  return readable<BatchLoadingState>(
    {
      findings: [],
      isLoading: false,
      hasMore: totalCount > 0,
      totalCount,
      loadedCount: 0,
      loadingProgress: initialLoadingProgress,
    },
    (set) => {
      let currentState: BatchLoadingState = {
        findings: [],
        isLoading: false,
        hasMore: totalCount > 0,
        totalCount,
        loadedCount: 0,
        loadingProgress: initialLoadingProgress,
      };

      let autoLoadTimeout: ReturnType<typeof setTimeout> | null = null;

      const loadBatch = async (lastDoc?: QueryDocumentSnapshot) => {
        if (currentState.isLoading || !currentState.hasMore) return;

        set((currentState = { ...currentState, isLoading: true }));

        try {
          const firestore = getFirestore(await firebaseApp);
          const collectionRef = collection(firestore, `organisations/${orgId}/security_findings`);

          let queryRef;
          if (schemaVersion === "v3") {
            const constraints: QueryConstraint[] = [
              where("securityFindingVersion", "==", reportVersion),
              where("securityFindingKind", "==", ANOMALOUS_PERMISSIONS_PAGE_KIND),
              limit(BATCH_SIZE),
            ];

            if (lastDoc) {
              constraints.push(startAfter(lastDoc));
            }

            queryRef = query(collectionRef, ...constraints);
          } else {
            const constraints: QueryConstraint[] = [
              where("security_finding_reference.security_finding_version", "==", reportVersion),
              orderBy("created", "desc"),
              limit(BATCH_SIZE),
            ];

            if (lastDoc) {
              constraints.push(startAfter(lastDoc));
            }

            queryRef = query(collectionRef, ...constraints);
          }

          const snapshot = await getDocs(queryRef);
          const processed = processSecurityFindings(snapshot.docs, schemaVersion);
          // v2: filter after load; `loadedCount` still counts raw docs so the loader reads every one.
          const newFindings = onlyAnomalousPermissionsPageKind(processed, schemaVersion);

          const newLoadedCount = currentState.loadedCount + snapshot.docs.length;
          const hasMore = newLoadedCount < totalCount && snapshot.docs.length === BATCH_SIZE;
          const loadingProgress = totalCount > 0 ? Math.round((newLoadedCount / totalCount) * 100) : 100;

          const lastDocument = snapshot.docs[snapshot.docs.length - 1];

          set(
            (currentState = {
              findings: [...currentState.findings, ...newFindings],
              isLoading: false,
              hasMore,
              totalCount,
              loadedCount: newLoadedCount,
              lastDocument,
              loadingProgress,
            }),
          );

          // Auto-load next batch if there's more data
          if (hasMore) {
            autoLoadTimeout = setTimeout(() => {
              loadBatch(lastDocument);
            }, DELAY_BETWEEN_BATCHES);
          }
        } catch (error) {
          console.error("[batchSecurityFindings] Error loading batch:", error);
          set(
            (currentState = {
              ...currentState,
              isLoading: false,
              error: error instanceof Error ? error.message : "Unknown error",
            }),
          );
        }
      };

      // Start initial batch load automatically
      loadBatch();

      return () => {
        // Cleanup - clear any pending timeouts
        if (autoLoadTimeout) {
          clearTimeout(autoLoadTimeout);
        }
      };
    },
  );
};

const processSecurityFindings = (docs: QueryDocumentSnapshot[], schemaVersion: string): SecurityFinding[] => {
  return schemaVersion === "v3"
    ? map(docs, (doc) => {
        const data = doc.data();
        // v3 schema processing (same as before)
        const sensitiveTopics = data.resources
          .map(
            (resource: SecurityFindingResource) =>
              resource?.sensitiveTopics?.map((topic: SensitiveTopic) => ({
                sensitiveTopic: topic.sensitiveTopic,
                sensitivityScore: topic.sensitivityScore,
              })) || [],
          )
          .flat();
        const system = data.resources[0].resourceId.split(":")[0];

        let securityFindingKindBasedData = {};
        if (data.securityFindingKind === SecurityFindingKind.ANOMALOUS_PERMISSIONS_ON_SENSITIVE_DOCUMENT) {
          securityFindingKindBasedData = {
            numberOfMisconfiguredPermissions: data.numberOfMisconfiguredPermissions || 0,
            numberOfImpactedPrincipals: data.numberOfImpactedPrincipals || 0,
            totalNumberOfPermissions: data.totalNumberOfPermissions || 0,
            totalNumberOfPrincipals: data.totalNumberOfPrincipals || 0,
            permissions: data.permissions || [],
            recommendations: data.permissions?.map((p: Permission & { recommendedAction?: string }) => ({
              email: p.principalEmail,
              recommendedAction: p.recommendedAction,
            })),
          };
        } else if (data.securityFindingKind === SecurityFindingKind.DOCUMENT_ACCESSED_BY_EXTERNAL_PRINCIPAL) {
          securityFindingKindBasedData = summariseExternalPrincipals(data.permissions);
        } else if (data.securityFindingKind === SecurityFindingKind.LARGE_SCALE_COLLABORATION_ON_PERSONAL_DRIVE) {
          securityFindingKindBasedData = {
            documents: data.documents || [],
            numberOfDocumentsInPersonalDrive: data.numberOfDocumentsInPersonalDrive || 0,
            learnedThreshold: data.learnedThreshold || 0,
          };
        } else if (data.securityFindingKind === SecurityFindingKind.PERMISSIONS_MANAGED_THROUGH_SECONDARY_IDENTITY) {
          securityFindingKindBasedData = {
            permissions: data.permissions || [],
            totalNumberOfPermissions: data.permissions?.length || 0,
          };
        } else if (data.securityFindingKind === SecurityFindingKind.PERSONAL_DRIVE_WITH_SENSITIVE_DOCUMENTS) {
          securityFindingKindBasedData = {
            sensitiveDocuments: data.sensitiveDocuments || [],
            permissionsToSensitiveDocuments: data.permissionsToSensitiveDocuments || [],
            totalNumberOfDocuments: data.totalNumberOfDocuments || 0,
            totalNumberOfSensitiveDocuments: data.totalNumberOfSensitiveDocuments || 0,
          };
        }
        return {
          id: doc.id,
          system,
          securityFindingId: data.securityFindingId,
          securityFindingVersion: data.securityFindingVersion,
          securityFindingKind: data.securityFindingKind,
          modified: data.modified,
          severity: data.severity,
          severityLevel: mapSeverityScoreToLevel(data.severity),
          confidence: data.confidence,
          resources: data.resources,
          created: data.created,
          sensitiveTopics,
          status: data.status,
          ...securityFindingKindBasedData,
        } as SecurityFinding;
      })
    : map(docs, (doc) => {
        // v1, v2 schema processing (same as before)
        const data = doc.data();
        return {
          id: doc.id,
          system: data.security_finding_reference?.system,
          securityFindingId: data.security_finding_reference?.security_finding_id,
          securityFindingVersion: data.security_finding_reference?.security_finding_version,
          securityFindingKind: data.security_finding_reference?.security_finding_kind,
          modified: data.modified,
          severity: data.severity,
          severityLevel: mapSeverityScoreToLevel(data.severity),
          confidence: data.confidence,
          resources: [
            {
              resourceId: doc.id,
              resourceVersion: data.source_features?.version,
              createdAt: data.source_features?.created_at,
              modifiedAt: data.source_features?.modified_at,
              name: data.source_features?.document_name,
              path: data.source_features?.document_path,
              link: data.source_features?.document_link,
              owner: data.source_features?.author_email,
              ownerType: "user",
              ownerEmail: data.source_features?.author_email,
              isSensitive: data.source_features?.is_sensitive,
              sensitiveTopics: data.source_features?.sensitive_topics?.map((topic: any) => ({
                sensitiveTopic: topic.sensitive_topic,
                sensitivityScore: topic.sensitivity_score,
              })),
            },
          ],
          numberOfMisconfiguredPermissions: data.stats?.number_of_misconfigured_permissions || 0,
          numberOfImpactedPrincipals: data.stats?.number_of_impacted_principals || 0,
          totalNumberOfPermissions: data.stats?.total_number_of_permissions || 0,
          totalNumberOfPrincipals: data.stats?.total_number_of_principals || 0,
          created: data.created,
          recommendations: data.recommendations,
          sensitiveTopics: data.source_features?.sensitive_topics?.map((topic: any) => ({
            sensitiveTopic: topic.sensitive_topic,
            sensitivityScore: topic.sensitivity_score,
          })),
          isSensitiveDocument: data.source_features?.is_sensitive,
          status: data.status,
        } as SecurityFinding;
      });
};

// New batch loading store
export const batchOrgSecurityFindingsStore = chainStores(
  derived(
    [selectedOrgIdStore, selectedReportVersionStore, selectedSchemaVersionStore, selectedVersionFindingCountStore],
    (both) => both,
  ),
  { findings: [], isLoading: false, hasMore: false, totalCount: 0, loadedCount: 0 } as BatchLoadingState,
  ([selectedOrgId, selectedReportVersion, selectedSchemaVersion, totalCount]) => {
    if (!selectedOrgId || !selectedReportVersion || !selectedSchemaVersion || totalCount === undefined) {
      return readable({
        findings: [],
        isLoading: false,
        hasMore: false,
        totalCount: 0,
        loadedCount: 0,
      } as BatchLoadingState);
    }

    return createBatchSecurityFindingsStore(selectedOrgId, selectedReportVersion, selectedSchemaVersion, totalCount);
  },
);

// Computed store for processed findings with impact scores
export const processedBatchOrgSecurityFindingsStore = derived([batchOrgSecurityFindingsStore], ([batchState]) => {
  if (!batchState.findings || batchState.findings.length === 0) {
    return batchState;
  }

  return {
    ...batchState,
    findings: computeImpactScorePerKind(batchState.findings),
  };
});
